import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useGetArtistPortalTrackDailyQuery } from '../../../../redux/api/fymAPI';
import { ArtistPortalRangeDays } from '../../../../redux/api/fymAPI.types';
import { buildStreamingChartData } from '../buildStreamingChartData';
import { sourceLook } from '../streamingSources';
import { streamingBlockMessages as messages } from '../streamingBlockMessages';
import { StreamingBlock } from './StreamingBlock';
import { StreamingLineChart } from './StreamingLineChart';

interface TrackSourceChartProps {
  accountId: number;
  integrationType: string;
  externalId: string;
  days: ArtistPortalRangeDays;
}

/**
 * The streams per day of one track on one source (design d2). Each source has its own chart. It
 * sits inside the track block, so it is a nested box.
 */
const TrackSourceChartComponent: React.FC<TrackSourceChartProps> = ({
  accountId,
  integrationType,
  externalId,
  days,
}) => {
  const intl = useIntl();
  const { data, isLoading, error, refetch } = useGetArtistPortalTrackDailyQuery({
    accountId,
    integrationType,
    externalId,
    days,
  });
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const chart = useMemo(
    () =>
      buildStreamingChartData({
        series: [
          {
            integrationType,
            points: (data?.series ?? []).map((point) => ({ date: point.date, streams: point.streams })),
          },
        ],
        metric: 'streams',
        split: 'source',
        hasDistributor: false,
        off: [],
      }),
    [data, integrationType]
  );

  return (
    <StreamingBlock
      title={intl.formatMessage(messages.trackDaily, { source: sourceLook(integrationType).name })}
      isLoading={isLoading}
      error={error}
      retry={retry}
      nested
    >
      <StreamingLineChart data={chart} totalLabel={sourceLook(integrationType).name} height={200} />
    </StreamingBlock>
  );
};

export const TrackSourceChart = TypedMemo(TrackSourceChartComponent);
